/**
 * ============================================================================
 * TRANSIENT: Appointment Config Page (appointment-config-page)
 * Type: poc
 * Amplitude key: none
 * End date: 2026-12-31
 * PR: #TODO_AFTER_PR_CREATION_appointment-config-page
 * ============================================================================
 */

import { Box, Divider, Stack, Typography } from '@evaneos/b2b-ui';
import { differenceInMinutes } from 'date-fns';
import { useIntl } from 'react-intl';

import { buildGoogleOAuthUrl } from '../../resources/appointment/operations';

import { AppointmentContactPartial } from './partials/AppointmentContactPartial';
import { AppointmentDisconnectedStatePartial } from './partials/AppointmentDisconnectedStatePartial';
import { AppointmentGoogleConnectionPartial } from './partials/AppointmentGoogleConnectionPartial';
import { AppointmentPageHeaderPartial } from './partials/AppointmentPageHeaderPartial';
import { AppointmentParamsPartial } from './partials/AppointmentParamsPartial';
import { AppointmentSidebarPartial } from './partials/AppointmentSidebarPartial';
import { AppointmentSlotsPartial } from './partials/AppointmentSlotsPartial';
import { AppointmentWipContentPartial } from './partials/AppointmentWipContentPartial';
import { useAppointment } from './useAppointment';

export const AppointmentPage = (): JSX.Element | null => {
    const {
        isLoading,
        isConnected,
        activeTab,
        days,
        defaultCallType,
        slotDurationInMinutes,
        travelerReminder,
        timezone,
        activeSection,
        activeDays,
        hasNoSlots,
        lastSavedAt,
        slotsRef,
        contactRef,
        paramsRef,
        setActiveTab,
        setDefaultCallType,
        setTravelerReminder,
        setTimezone,
        handleToggleDay,
        handleSlotStartChange,
        handleSlotEndChange,
        handleDeleteSlot,
        handleAddSlot,
        handleDurationChange,
        handleSectionClick,
    } = useAppointment();

    const { formatMessage } = useIntl();

    if (isLoading) return null;

    // Feedback de confirmation d'action : au-delà d'1h, l'info n'apporte plus de valeur → masquée (BBIZ-607)
    const diffMin = differenceInMinutes(new Date(), lastSavedAt);
    let savedLabel: string | null = null;
    if (diffMin < 1) {
        savedLabel = formatMessage({ id: 'appointment.saved_at.just_now' });
    } else if (diffMin < 60) {
        savedLabel = formatMessage({ id: 'appointment.saved_at.minutes_ago' }, { count: diffMin });
    }

    return (
        <Box
            data-spec-id="agenda-page"
            sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
        >
            <AppointmentPageHeaderPartial
                activeTab={activeTab}
                isConnected={isConnected}
                onTabChange={setActiveTab}
            />

            <Box sx={{ flex: 1, bgcolor: 'var(--color-beige)', overflow: 'auto' }}>
                {!isConnected && (
                    <AppointmentDisconnectedStatePartial
                        onConnect={() => window.location.assign(buildGoogleOAuthUrl())}
                    />
                )}
                {isConnected && activeTab === 'rendez-vous' && <AppointmentWipContentPartial />}
                {isConnected && activeTab === 'disponibilites' && (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '306px 1fr',
                            gap: 3,
                            p: 4,
                            alignItems: 'start',
                        }}
                    >
                        <AppointmentSidebarPartial
                            activeSection={activeSection}
                            activeDays={activeDays}
                            defaultCallType={defaultCallType}
                            slotDurationInMinutes={slotDurationInMinutes}
                            travelerReminder={travelerReminder}
                            onSectionClick={handleSectionClick}
                        />
                        <Box
                            sx={{
                                bgcolor: 'background.paper',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                p: 3,
                            }}
                        >
                            {savedLabel && (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        data-spec-id="saved-indicator"
                                    >
                                        {savedLabel}
                                    </Typography>
                                </Box>
                            )}
                            <Stack spacing={6} divider={<Divider />}>
                                <Box id="slots" ref={slotsRef}>
                                    <AppointmentSlotsPartial
                                        days={days}
                                        timezone={timezone}
                                        hasNoSlots={hasNoSlots}
                                        onToggleDay={handleToggleDay}
                                        onSlotStartChange={handleSlotStartChange}
                                        onSlotEndChange={handleSlotEndChange}
                                        onDeleteSlot={handleDeleteSlot}
                                        onAddSlot={handleAddSlot}
                                        onTimezoneChange={setTimezone}
                                    />
                                </Box>
                                <Box id="contact" ref={contactRef}>
                                    <AppointmentContactPartial
                                        value={defaultCallType}
                                        onChange={setDefaultCallType}
                                    />
                                </Box>
                                <Box id="params" ref={paramsRef}>
                                    <AppointmentParamsPartial
                                        slotDurationInMinutes={slotDurationInMinutes}
                                        travelerReminder={travelerReminder}
                                        onDurationChange={handleDurationChange}
                                        onTravelerReminderChange={setTravelerReminder}
                                    />
                                </Box>
                                <Box>
                                    <AppointmentGoogleConnectionPartial />
                                </Box>
                            </Stack>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
};
